export function normalizeValue(value, fieldConfig) {
    if (value === null || value === undefined) {
        return null;
    }
    switch (fieldConfig.type) {
        case "text":
            return normalizeText(value);
        case "classification":
            return normalizeText(value);
        case "options":
            return normalizeText(value);
        case "multipleOptions":
            return normalizeMultipleOptions(value);
        case "file":
            return normalizeFile(value);
        case "scheduling":
            return normalizeScheduling(value);
        case "yesno":
            return normalizeYesNo(value);
        case "fullName":
            return normalizeFullName(value);
        case "email":
            return normalizeEmail(value);
        case "date":
            return normalizeDate(value);
        case "address":
            return normalizeAddress(value);
        case "phone":
            return normalizePhone(value);
        case "composedList":
            return normalizeComposedList(value);
        case "composed":
            return normalizeComposed(value);
        case "age":
            return normalizeAge(value);
        case "timeOfDay":
            return normalizeTimeOfDay(value);
        case "duration":
            return normalizeDuration(value);
        case "number":
            return normalizeNumber(value);
        case "integer":
            return normalizeInteger(value);
        // case "money":
        //   return normalizeMoney(value);
        case "percentage":
            return normalizePercentage(value);
        case "url":
            return normalizeUrl(value);
        default:
            return value;
    }
}
function normalizeText(value) {
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    return null;
}
function normalizeMultipleOptions(value) {
    // Already valid string array
    if (Array.isArray(value) && value.every(v => typeof v === "string")) {
        return value;
    }
    // Array with non-strings - convert
    if (Array.isArray(value)) {
        return value.map(v => String(v));
    }
    if (typeof value === "string") {
        // Split by comma, semicolon, or newline
        return value.split(/[,;\n]+/).map(v => v.trim()).filter(v => v.length > 0);
    }
    return null;
}
function normalizeScheduling(value) {
    if (typeof value === "object" && value !== null) {
        return value;
    }
    return null;
}
function normalizeYesNo(value) {
    if (typeof value === "boolean") {
        return value;
    }
    if (typeof value === "string") {
        const lower = value.toLowerCase().trim();
        if (["yes", "y", "true", "t", "1", "on", "checked"].includes(lower)) {
            return true;
        }
        if (["no", "n", "false", "f", "0", "off", "unchecked"].includes(lower)) {
            return false;
        }
        return null;
    }
    if (typeof value === "number") {
        if (value === 1)
            return true;
        if (value === 0)
            return false;
        return null;
    }
    return null;
}
function normalizeFullName(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "firstName" in value && "lastName" in value) {
        if (typeof value.firstName === "string" && typeof value.lastName === "string" &&
            (value.middleName === undefined || typeof value.middleName === "string")) {
            return value;
        }
        // Has the right shape but wrong types - convert
        return {
            firstName: String(value.firstName || "").trim(),
            ...(value.middleName ? { middleName: String(value.middleName).trim() } : {}),
            lastName: String(value.lastName || "").trim(),
        };
    }
    // Try to parse from string
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed)
            return null;
        // Split by spaces, commas, or other common separators
        const parts = trimmed.split(/[\s,]+/).filter(p => p.length > 0);
        if (parts.length === 0) {
            return null;
        }
        if (parts.length === 1) {
            // Single name - treat as first name, lastName empty
            return { firstName: parts[0], lastName: "" };
        }
        if (parts.length === 2) {
            // Two parts - first and last name
            return { firstName: parts[0], lastName: parts[1] };
        }
        // Three or more parts - first, middle (joined), last
        return {
            firstName: parts[0],
            middleName: parts.slice(1, -1).join(" "),
            lastName: parts[parts.length - 1],
        };
    }
    return null;
}
function normalizeEmail(value) {
    if (typeof value === "string") {
        // Basic cleanup: trim and lowercase
        const cleaned = value.trim().toLowerCase();
        if (cleaned.includes("@")) {
            return cleaned;
        }
    }
    return null;
}
function normalizeDate(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "day" in value && "month" in value && "year" in value) {
        if (typeof value.day === "number" && typeof value.month === "number" && typeof value.year === "number") {
            return value;
        }
        // Has the right shape but wrong types - convert
        const day = Number(value.day);
        const month = Number(value.month);
        const year = Number(value.year);
        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return { day, month, year };
        }
    }
    // Try to parse from string
    if (typeof value === "string") {
        const trimmed = value.trim();
        // Try ISO format: YYYY-MM-DD
        const isoMatch = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (isoMatch) {
            return { year: parseInt(isoMatch[1]), month: parseInt(isoMatch[2]), day: parseInt(isoMatch[3]) };
        }
        // Try DD/MM/YYYY or DD-MM-YYYY
        const dmyMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
        if (dmyMatch) {
            let year = parseInt(dmyMatch[3]);
            if (year < 100)
                year += year > 50 ? 1900 : 2000;
            return { day: parseInt(dmyMatch[1]), month: parseInt(dmyMatch[2]), year };
        }
        // Try MM/DD/YYYY (US format) - harder to distinguish, skip for now
        // Try Date object parsing as fallback
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
            return {
                day: parsed.getDate(),
                month: parsed.getMonth() + 1,
                year: parsed.getFullYear(),
            };
        }
    }
    // Try from Date object or timestamp
    if (value instanceof Date) {
        return { day: value.getDate(), month: value.getMonth() + 1, year: value.getFullYear() };
    }
    if (typeof value === "number" && value > 0) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
            return { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };
        }
    }
    return null;
}
function normalizeAddress(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "addressLine1" in value && "city" in value) {
        if (typeof value.addressLine1 === "string" && typeof value.city === "string" &&
            (value.state === undefined || typeof value.state === "string") &&
            (value.zip === undefined || typeof value.zip === "string") &&
            (value.country === undefined || typeof value.country === "string")) {
            return value;
        }
        // Has the right shape but wrong types - convert
        return {
            addressLine1: String(value.addressLine1 || "").trim(),
            city: String(value.city || "").trim(),
            ...(value.state ? { state: String(value.state).trim() } : {}),
            ...(value.zip ? { zip: String(value.zip).trim() } : {}),
            ...(value.country ? { country: String(value.country).trim() } : {}),
        };
    }
    // Can't reliably parse address from string without more context
    if (typeof value === "string" && value.trim()) {
        // Return as addressLine1 only, other parts empty
        return { addressLine1: value.trim(), city: "" };
    }
    return null;
}
function replaceSeparatorCharactersInPhone(value) {
    return {
        countryCode: value.countryCode?.replace(/[\s\-\.\(\)\/]+/g, "")?.replace(/^\+/, ""),
        areaCode: value.areaCode?.replace(/[\s\-\.\(\)\/]+/g, ""),
        number: value.number?.replace(/[\s\-\.\(\)\/]+/g, ""),
    };
}
function normalizePhone(value) {
    if (value == null)
        return null;
    // Pass objects through, preserving reference if already the correct shape
    if (typeof value === "object" && value !== null) {
        if (!("number" in value))
            return null;
        if (typeof value.number === "string" &&
            (value.countryCode === undefined || typeof value.countryCode === "string") &&
            (value.areaCode === undefined || typeof value.areaCode === "string")) {
            return replaceSeparatorCharactersInPhone(value);
        }
        // Coerce wrong-typed fields
        return replaceSeparatorCharactersInPhone({
            ...(value.countryCode != null ? { countryCode: String(value.countryCode).replace(/^\+/, "").trim() } : {}),
            ...(value.areaCode != null ? { areaCode: String(value.areaCode).trim() } : {}),
            number: String(value.number).trim(),
        });
    }
    // Coerce any non-string primitive to a string and attempt parsing
    const str = typeof value === "string" ? value : String(value);
    const trimmed = str.trim();
    if (!trimmed)
        return null;
    // Strip leading + (E.164 prefix)
    const withoutPlus = trimmed.replace(/^\+/, "");
    // Split on common phone separators; parens count as separators.
    // Strip only non-alphanumeric special characters (e.g. * #) from each part,
    // but preserve letters so the validator can reject them with a specific message.
    const parts = withoutPlus
        .split(/[\s\-\.\(\)\/]+/)
        .map(p => p.replace(/[^a-zA-Z0-9]/g, ""))
        .filter(p => p.length > 0);
    if (parts.length === 0)
        return null;
    if (parts.length === 1)
        return { number: parts[0] };
    if (parts.length === 2)
        return { countryCode: parts[0]?.replace(/^\+/, ""), number: parts[1] };
    // 3+ parts: first = countryCode, second = areaCode, rest concatenated as number
    return replaceSeparatorCharactersInPhone({
        countryCode: parts[0],
        areaCode: parts[1],
        number: parts.slice(2).join(""),
    });
}
function normalizeComposedList(value) {
    if (Array.isArray(value)) {
        return value;
    }
    return null;
}
function normalizeComposed(value) {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        return value;
    }
    return null;
}
function normalizeAge(value) {
    // Already valid integer
    if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
        return value;
    }
    // Float - floor it
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
        return Math.floor(value);
    }
    if (typeof value === "string") {
        const parsed = parseInt(value.trim(), 10);
        if (!isNaN(parsed) && parsed >= 0) {
            return parsed;
        }
    }
    return null;
}
function normalizeTimeOfDay(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "hour" in value && "minute" in value) {
        if (typeof value.hour === "number" && typeof value.minute === "number") {
            return value;
        }
        // Has the right shape but wrong types - convert
        const hour = Number(value.hour);
        const minute = Number(value.minute);
        if (!isNaN(hour) && !isNaN(minute)) {
            return { hour, minute };
        }
    }
    // Try to parse from string
    if (typeof value === "string") {
        const trimmed = value.trim().toLowerCase();
        // Match HH:MM or H:MM format, with optional AM/PM
        const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i);
        if (timeMatch) {
            let hour = parseInt(timeMatch[1]);
            const minute = parseInt(timeMatch[2]);
            const ampm = timeMatch[3]?.toLowerCase();
            if (ampm === "pm" && hour < 12)
                hour += 12;
            if (ampm === "am" && hour === 12)
                hour = 0;
            return { hour, minute };
        }
        // Match just hour with AM/PM
        const hourMatch = trimmed.match(/^(\d{1,2})(?:\s*(am|pm))$/i);
        if (hourMatch) {
            let hour = parseInt(hourMatch[1]);
            const ampm = hourMatch[2]?.toLowerCase();
            if (ampm === "pm" && hour < 12)
                hour += 12;
            if (ampm === "am" && hour === 12)
                hour = 0;
            return { hour, minute: 0 };
        }
        // Just a number - treat as hour
        const justHour = parseInt(trimmed);
        if (!isNaN(justHour) && justHour >= 0 && justHour <= 23) {
            return { hour: justHour, minute: 0 };
        }
    }
    return null;
}
function normalizeDuration(value) {
    // Already a number (minutes)
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
        return value;
    }
    if (typeof value === "string") {
        const trimmed = value.trim().toLowerCase();
        // Match patterns like "1h 30m", "1 hour 30 minutes", "90 minutes", "1.5 hours"
        let totalMinutes = 0;
        // Hours
        const hoursMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/);
        if (hoursMatch) {
            totalMinutes += parseFloat(hoursMatch[1]) * 60;
        }
        // Minutes
        const minutesMatch = trimmed.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m(?![os]))/);
        if (minutesMatch) {
            totalMinutes += parseFloat(minutesMatch[1]);
        }
        if (totalMinutes > 0) {
            return Math.round(totalMinutes);
        }
        // Just a number - assume minutes
        const justNumber = parseFloat(trimmed);
        if (!isNaN(justNumber) && justNumber >= 0) {
            return justNumber;
        }
    }
    return null;
}
function normalizeNumber(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        // Remove common formatting (commas, spaces)
        const cleaned = value.trim().replace(/[,\s]/g, "");
        const parsed = parseFloat(cleaned);
        if (!isNaN(parsed) && Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
}
function normalizeInteger(value) {
    if (typeof value === "number" && Number.isInteger(value)) {
        return value;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return Math.round(value);
    }
    if (typeof value === "string") {
        const cleaned = value.trim().replace(/[,\s]/g, "");
        const parsed = parseInt(cleaned, 10);
        if (!isNaN(parsed)) {
            return parsed;
        }
    }
    return null;
}
// The money field is not being used as of now.
// function normalizeMoney(value: any): MoneyValue | null {
//   // Already in correct format - return as-is
//   if (typeof value === "object" && value !== null && "currency" in value && "amount" in value) {
//     if (typeof value.currency === "string" && typeof value.amount === "number") {
//       return value as MoneyValue;
//     }
//     // Has the right shape but wrong types - convert
//     const amount = Number(value.amount);
//     if (!isNaN(amount)) {
//       return { currency: String(value.currency).toUpperCase(), amount };
//     }
//   }
//   // Try to parse from string
//   if (typeof value === "string") {
//     const trimmed = value.trim();
//     // Match patterns like "$123.45", "USD 123.45", "123.45 EUR"
//     const currencySymbols: Record<string, string> = {
//       "$": "USD", "€": "EUR", "£": "GBP", "¥": "JPY", "₹": "INR",
//       "C$": "CAD", "A$": "AUD", "CHF": "CHF", "kr": "SEK"
//     };
//     // Check for currency symbol at start
//     for (const [symbol, code] of Object.entries(currencySymbols)) {
//       if (trimmed.startsWith(symbol)) {
//         const amountStr = trimmed.slice(symbol.length).replace(/[,\s]/g, "");
//         const amount = parseFloat(amountStr);
//         if (!isNaN(amount)) {
//           return { currency: code, amount };
//         }
//       }
//     }
//     // Check for currency code at start or end
//     const codeMatch = trimmed.match(/^([A-Z]{3})\s*([\d,.\s]+)$|^([\d,.\s]+)\s*([A-Z]{3})$/);
//     if (codeMatch) {
//       const currency = codeMatch[1] || codeMatch[4];
//       const amountStr = (codeMatch[2] || codeMatch[3]).replace(/[,\s]/g, "");
//       const amount = parseFloat(amountStr);
//       if (!isNaN(amount)) {
//         return { currency, amount };
//       }
//     }
//     // Just a number - default to USD
//     const justNumber = parseFloat(trimmed.replace(/[,\s]/g, ""));
//     if (!isNaN(justNumber)) {
//       return { currency: "USD", amount: justNumber };
//     }
//   }
//   // Just a number
//   if (typeof value === "number" && Number.isFinite(value)) {
//     return { currency: "USD", amount: value };
//   }
//   return null;
// }
function normalizePercentage(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string") {
        // Remove % sign and parse
        const cleaned = value.trim().replace(/%/g, "").trim();
        const parsed = parseFloat(cleaned);
        if (!isNaN(parsed) && Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
}
function normalizeUrl(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "domain" in value) {
        if (typeof value.protocol === "string" && typeof value.domain === "string" &&
            typeof value.path === "string" && typeof value.query === "string") {
            return value;
        }
        // Has the right shape but wrong/missing types - convert
        return {
            protocol: String(value.protocol || "https"),
            domain: String(value.domain || ""),
            path: String(value.path || ""),
            query: String(value.query || ""),
        };
    }
    // Try to parse from string
    if (typeof value === "string") {
        const trimmed = value.trim();
        if (!trimmed)
            return null;
        try {
            // Add protocol if missing
            let urlString = trimmed;
            if (!urlString.match(/^[a-zA-Z]+:\/\//)) {
                urlString = "https://" + urlString;
            }
            const url = new URL(urlString);
            return {
                protocol: url.protocol.replace(":", ""),
                domain: url.hostname,
                path: url.pathname === "/" ? "" : url.pathname,
                query: url.search ? url.search.slice(1) : "",
            };
        }
        catch {
            // If URL parsing fails, try basic extraction
            const domainMatch = trimmed.match(/^(?:([a-zA-Z]+):\/\/)?([^\/\?\#]+)(.*)$/);
            if (domainMatch) {
                const fullPath = domainMatch[3] || "";
                const [pathPart, queryPart] = fullPath.split("?");
                return {
                    protocol: domainMatch[1] || "https",
                    domain: domainMatch[2],
                    path: pathPart || "",
                    query: queryPart || "",
                };
            }
        }
    }
    return null;
}
function normalizeFile(value) {
    // Already in correct format - return as-is
    if (typeof value === "object" && value !== null && "fileName" in value && "fileUri" in value) {
        if (typeof value.fileName === "string" && typeof value.fileUri === "string" &&
            (value.mimeType === undefined || typeof value.mimeType === "string")) {
            return value;
        }
        // Has the right shape but wrong types - convert
        return {
            fileName: String(value.fileName || ""),
            mimeType: String(value.mimeType || ""),
            fileUri: String(value.fileUri || ""),
        };
    }
    // Can't reasonably convert other types to file
    return null;
}
